/**
 * useSalesLeadsData Hook
 *
 * Fetches MTD Good Leads counts from /sales-leads-pacing for the
 * Sales Department Analysis card on the Forecast page, and joins per-
 * salesperson lead counts (VinSolutions) with units sold (DealerBuilt) to
 * power the Closing Ratio drill-down.
 *
 * Join strategy: ID-first, then normalized-name fallback.
 *   1. VinSolutions useraccess.DMSSalesRepID matches DealerBuilt lydeal.salesid1
 *      (surfaced on the salesperson record as `dmsSalesRepId`). When both sides
 *      carry the ID we join on that — collapses Tim/Timothy, Bob/Robert,
 *      hyphens, and other name variants.
 *   2. If either side is missing the ID, fall back to lowercased + whitespace-
 *      collapsed display name. VinSolutions useraccess provides FirstName +
 *      LastName already in "FIRST LAST" form, and the DealerBuilt loader's
 *      formatManagerName canonicalises sales1name the same way.
 * Truly mismatched rows surface as leads-only or units-only — informational,
 * never blocking.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getSalesLeadsPacing,
  getSalespersonPerformance,
  SalesLeadsPacingResponse,
} from '../services/api';

interface UseSalesLeadsDataParams {
  locationId: number;
  year: number;
  month: number;
  enabled: boolean;
}

export interface ClosingRatioRow {
  /** Display name to render. Prefers DealerBuilt name when both sides match. */
  name: string;
  /** Distinct Good Leads MTD that this salesperson worked, from VinSolutions. */
  leads: number;
  /** New + Used units sold MTD (swap and wholesale already excluded by DealerBuilt loader). */
  units: number;
  /** units / leads × 100, rounded to one decimal. null when leads === 0. */
  ratioPct: number | null;
  /** True when only one of (leads, units) > 0 — caller can render a flag. */
  unmatched: boolean;
}

interface UseSalesLeadsDataResult {
  data: SalesLeadsPacingResponse | null;
  loading: boolean;
  error: string | null;
  /** Sorted by units desc by default. */
  closingRatioRows: ClosingRatioRow[];
  /** sum(units) / mtdGoodLeads * 100 — null if no good leads or no salesperson data. */
  locationClosingRatioPct: number | null;
  /** Total New+Used units sold across all salespeople for the location. */
  locationUnits: number;
  /** Count of rows where leads or units is missing — surfaces match issues to the UI. */
  unmatchedCount: number;
}

function normalizeName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function useSalesLeadsData({
  locationId,
  year,
  month,
  enabled,
}: UseSalesLeadsDataParams): UseSalesLeadsDataResult {
  const [data, setData] = useState<SalesLeadsPacingResponse | null>(null);
  const [salespeopleUnits, setSalespeopleUnits] = useState<
    Array<{ name: string; dmsSalesRepId: string; units: number }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [leadsRes, perfRes] = await Promise.all([
        getSalesLeadsPacing(locationId, year, month),
        getSalespersonPerformance(locationId, year, month),
      ]);

      if (leadsRes.success && leadsRes.data) {
        setData(leadsRes.data);
      } else {
        setError(leadsRes.error || 'Failed to load sales leads data');
        setData(null);
      }

      if (perfRes.success && perfRes.data) {
        // raw.salespeople is `any[]` per the api.ts type; we only need name,
        // dmsSalesRepId (for ID-based join), and units.
        const rows = (perfRes.data.salespeople || []).map((sp: any) => ({
          name: String(sp.salespersonName || ''),
          dmsSalesRepId: sp.dmsSalesRepId ? String(sp.dmsSalesRepId) : '',
          units: Number(sp.newUnits || 0) + Number(sp.usedUnits || 0),
        }));
        setSalespeopleUnits(rows);
      } else {
        setSalespeopleUnits([]);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setData(null);
      setSalespeopleUnits([]);
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setData(null);
      setSalespeopleUnits([]);
      setError(null);
      return;
    }
    fetchData();
  }, [locationId, year, month, enabled, fetchData]);

  const closingRatioRows = useMemo<ClosingRatioRow[]>(() => {
    // Row buckets keyed first by DMS ID when both sides carry it, otherwise
    // by normalized name. The key encodes which join channel was used so we
    // never accidentally collapse two distinct people who happen to share a
    // normalized name across the two sources.
    type Bucket = {
      leads: number;
      units: number;
      // DealerBuilt name preferred when available (more authoritative for
      // payroll-style display); falls back to VinSolutions name.
      dmsName?: string;
      vinName?: string;
    };
    const idKey = (id: string) => `id:${id}`;
    const nameKey = (n: string) => `name:${normalizeName(n)}`;
    const buckets = new Map<string, Bucket>();

    // Pass 1: leads (VinSolutions). dmsSalesRepId comes from useraccess.
    // Skip zero-lead rows: since the Sales Funnel loader change, the breakdown
    // also carries visits-only / active-only salespeople (mtdLeads = 0); the
    // Closing Ratio table keeps its original "has MTD leads" population.
    for (const sp of data?.salesPersonBreakdown || []) {
      if (!sp.displayName || !sp.mtdLeads) continue;
      const key = sp.dmsSalesRepId ? idKey(sp.dmsSalesRepId) : nameKey(sp.displayName);
      const existing = buckets.get(key);
      if (existing) {
        existing.leads += sp.mtdLeads || 0;
        if (!existing.vinName) existing.vinName = sp.displayName;
      } else {
        buckets.set(key, {
          leads: sp.mtdLeads || 0,
          units: 0,
          vinName: sp.displayName,
        });
      }
    }

    // Pass 2: units (DealerBuilt). Try ID-key first; fall back to name-key.
    // If a bucket already exists under either key the units merge in;
    // otherwise create a fresh bucket on the most-specific key we have.
    for (const u of salespeopleUnits) {
      if (!u.name || u.name === 'Unassigned' || u.name === 'UNKNOWN') continue;

      const idK = u.dmsSalesRepId ? idKey(u.dmsSalesRepId) : null;
      const nameK = nameKey(u.name);

      let target: string | null = null;
      if (idK && buckets.has(idK)) target = idK;
      else if (buckets.has(nameK)) target = nameK;

      if (target) {
        const b = buckets.get(target)!;
        b.units += u.units;
        if (!b.dmsName) b.dmsName = u.name;
      } else {
        buckets.set(idK || nameK, {
          leads: 0,
          units: u.units,
          dmsName: u.name,
        });
      }
    }

    const rows: ClosingRatioRow[] = [];
    buckets.forEach((b) => {
      const displayName = b.dmsName || b.vinName || '';
      const ratioPct = b.leads > 0 ? Math.round((b.units / b.leads) * 1000) / 10 : null;
      rows.push({
        name: displayName,
        leads: b.leads,
        units: b.units,
        ratioPct,
        unmatched: b.leads === 0 || b.units === 0,
      });
    });

    rows.sort((a, b) => b.units - a.units || b.leads - a.leads || a.name.localeCompare(b.name));
    return rows;
  }, [data, salespeopleUnits]);

  const locationUnits = useMemo(
    () => closingRatioRows.reduce((sum, r) => sum + r.units, 0),
    [closingRatioRows],
  );

  const locationClosingRatioPct = useMemo(() => {
    const totalLeads = data?.mtdGoodLeads || 0;
    if (totalLeads === 0 || locationUnits === 0) return null;
    return Math.round((locationUnits / totalLeads) * 1000) / 10;
  }, [data, locationUnits]);

  const unmatchedCount = useMemo(
    () => closingRatioRows.filter((r) => r.unmatched).length,
    [closingRatioRows],
  );

  return {
    data,
    loading,
    error,
    closingRatioRows,
    locationClosingRatioPct,
    locationUnits,
    unmatchedCount,
  };
}
